import {IDeal, NewDealSuggestion} from "../../interfaces/IDeal";
import {
    AccordionButton,
    AccordionItem,
    AccordionPanel,
    Box,
    Button,
    Checkbox,
    CheckboxGroup,
    Collapse,
    Flex,
    Heading,
    Text,
    VStack,
    Wrap
} from "@chakra-ui/react";
import {Table, Tbody, Td, Th, Thead, Tr,} from 'react-super-responsive-table'

import {useEffect, useMemo, useState} from "react";
import {DayHours, formatWeekdayList, groupConsecutiveDaysByWorkingHours, WorkingHoursMap} from "../../tools/groupHours";
import moment from "moment";
import 'moment/locale/lv';
import {useLocale, useTranslations} from "next-intl";
import TimeView from "../ui/TimeView";
import ConfirmDiscardDialog from "./ConfirmDiscardDialog";
import {addRegionToLocale} from "../tools/momentLocale";
import {get} from "../tools/safeArray";
import {HideableField} from "./HideableField";
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';
import styles from "../ui/layout.module.css";
import {SelectToast} from "../ui/SelectToast";
import {TimePeriodPickerDialog} from "../ui/TimePeriodPickerDialog";
import {EndTimePicker, StartTimePicker} from "./NextDayCheckbox";
import {FieldLabel} from "../ui/widget/FieldLabel";
import {AnimatePresence} from "framer-motion";


export default function DealForm<T extends IDeal, D extends NewDealSuggestion>(
    {deal, updateDeal, deleteDeal, onDoneEditing, onStartEditing, editingThis, editingOther, initialDeal}:
        {deal: D, updateDeal: (deal: D) => void, deleteDeal: () => void, onDoneEditing: () => void, onStartEditing: () => void, editingThis: boolean, editingOther: boolean,
            initialDeal: T|undefined}
) {
    const [description, setDescription] = useState<string>(deal.description)
    const [conditions, setConditions] = useState<string>(deal.conditions)

    const locale = useLocale()
    useEffect(() => {
        moment.locale(addRegionToLocale(locale))
    }, [locale]);
    const daysOfWeek = [1,2,3,4,5,6,7]

    const [editingDays, setDays] = useState<WorkingHoursMap>(deal.days)

    const [editingWeekday, setEditingWeekday] = useState(-1)

    function startEditingWeekday(day: number) {
        const dayData = editingDays ? (editingDays[day] ? editingDays[day] : undefined) : undefined
        setState({
            start: dayData ? dayData[0] : undefined,
            end: dayData ? dayData[1] : undefined
        })
        setEditingWeekday(day)
    }

    const t = useTranslations("addbar")

    function saveWeekday(day: number) {
        setDays({
            ...editingDays,
            [day]: [state.start, state.end]
        } as WorkingHoursMap)
        setEditingWeekday(-1)
        setState({
            start: undefined,
            end: undefined
        })
    }

    function resetWeekday(day: number) {
        const res = {
            ...editingDays,
        }
        delete res[day]
        setDays(res)
        setEditingWeekday(-1)
        setState({
            start: undefined,
            end: undefined
        })
    }

    const [state, setState] = useState<DayHours>({
        start: undefined,
        end: undefined
    })

    const dealValid = useMemo(() => {
        if (!editingDays) return false
        const days = Object.values(editingDays)
        return description.length >= 5 && days.length > 0 && days.every(value =>
            value[0] !== undefined && value[1] !== undefined)
    }, [editingDays, description])

    const timeRowValid = useMemo(() => {
        return editingDays && state.start !== undefined && state.end !== undefined
    }, [state, editingDays])

    function saveDeal() {
        updateDeal({
            days: editingDays,
            description: description,
            conditions: conditions,
            original: deal.original,
        } as D)
        onDoneEditing()
    }

    const [editingDesc, setEditingDesc] = useState(false)
    const [editingConditions, setEditingConditions] = useState(false)

    const schedulesProcessed = useMemo(() => groupConsecutiveDaysByWorkingHours(editingDays).map(schedule => {
            return {
                weekdays: formatWeekdayList(schedule.days),
                time: schedule.hours
            }
        })
    , [editingDays])

    const [confirmOpen, setConfirmOpen] = useState(false)


    const [checkedDays, setCheckedDays] = useState<number[]>([])

    const [bulkEditing, setBulkEditing] = useState<number[]>([])

    function getBulkInitialValue(days: number[]): DayHours {
        const periods = days.reduce<(number|undefined)[]>((previousValue, currentValue) => {
            console.log("Reduce prev", previousValue, "Current",  currentValue)
            const period = editingDays[currentValue] ?? [undefined, undefined]
            console.log("Period", period)
            if (previousValue === undefined || previousValue[0] === undefined || previousValue[1] === undefined ) return period
            else if (previousValue[0] == period[0] && previousValue[1] == period[1]) return previousValue
            else return [undefined, undefined]
        }, [undefined, undefined])
        console.log("Initial result: ", periods)
        return {
            start: periods[0],
            end: periods[1]
        }
    }

    const [bulkInitialValue, setBulkInitialValue] = useState<DayHours|undefined>(undefined)

    return (
        <AccordionItem key={deal.description}
                       borderRadius="10px"
                       marginTop="5px"
                       borderWidth={editingThis ? "5px" : "2px"}
                       borderColor={editingThis ? "primary" : "default"}
        >
            <AccordionButton>
                <Flex direction="column" gap={3}>
                    <Wrap flex={1} alignItems="start">
                        <HideableField
                            value={description}
                            onChange={setDescription}
                            allowEdit={editingThis}
                            isShown={editingDesc}
                            onChangeVisibility={setEditingDesc}
                            placeholder={t("deal_field_hint")}
                            label={t("deal_field_label")}
                            isRequired={true}
                        />
                        <HideableField value={conditions}
                                       onChange={setConditions}
                                       allowEdit={editingThis}
                                       isShown={editingConditions}
                                       onChangeVisibility={setEditingConditions}
                                       placeholder={t("conditions_field_hint")}
                                       label={t("conditions_field_label")}/>
                    </Wrap>
                    {/*Current times*/}
                    <VStack flex={1} alignItems="start" gap={0}>
                        <FieldLabel text={t("time")}/>
                        <Heading size="sm">
                            {
                                schedulesProcessed.map(data =>
                                    <Box key={data.weekdays}>{data.weekdays}
                                        {" "}
                                        <TimeView time={data.time[0]} textStyle="normal"/>
                                        {" - "}
                                        <TimeView time={data.time[1]} textStyle="normal"/>
                                    </Box>
                                )
                            }
                        </Heading>
                        <Text fontSize="xs" flex={1} flexGrow={1}>

                        </Text>
                    </VStack>
                    {/*Edit button*/}
                    {!editingThis && !editingOther &&
                        <Button variant="secondary" onClick={onStartEditing} marginEnd="5px">{t("edit")}</Button>}
                </Flex>
            </AccordionButton>
            <AccordionPanel overflowX={"auto"}>

                <SelectToast checkedItems={checkedDays} onClear={setCheckedDays} onEdit={() => {
                    setBulkInitialValue(
                        getBulkInitialValue(checkedDays)
                    )
                    setBulkEditing(checkedDays)
                    setCheckedDays([])
                }}/>
                <TimePeriodPickerDialog text={bulkEditing.length > 0 ? formatWeekdayList(bulkEditing) : ""}
                                        initialValue={bulkInitialValue} onSave={(time) => {
                    bulkEditing.forEach(day => {
                        editingDays[day] = time
                    })
                    const copy = {};
                    Object.assign(copy, editingDays)
                    setDays(copy)
                }}/>
                <CheckboxGroup value={checkedDays} onChange={e => setCheckedDays(e as number[])}>

                    <Table className={styles.tableResponsive}>
                        <Thead>
                            <Tr>
                                <Th align="left" >{t("weekday")}</Th>
                                <Th align="left" key={1}>{t("time_from")}</Th>
                                <Th align="left" key={2}>{t("time_to")}</Th>
                                <Th align="left" key={3}>{t("edit")}</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            <ConfirmDiscardDialog
                                originalDealName={initialDeal?.description ?? ""}
                                open={confirmOpen}
                                onClosed={() => setConfirmOpen(false)}
                                onDiscard={() => {
                                    if (initialDeal) {
                                        setDescription(initialDeal.description)
                                        setDays(initialDeal.days)
                                        onDoneEditing()
                                        setEditingDesc(false)
                                        setEditingConditions(false)
                                    }
                                }}/>
                            {
                                daysOfWeek.map(day => {
                                    return (
                                        <Tr key={day}>
                                            <Td key={0}>
                                                {editingWeekday == -1 ? (
                                                    <Checkbox value={day.toString()}>
                                                        {moment().day(day).format("dddd")}
                                                    </Checkbox>
                                                ) : moment().day(day).format("dddd")}
                                            </Td>
                                            <Td key={1} style={{verticalAlign: "center"}}>
                                                {editingWeekday == day ? (
                                                    <StartTimePicker value={state} onChange={setState}/>
                                                ) : (
                                                    <TimeView time={get(editingDays[day], 0)} fallback="---"/>
                                                )}

                                            </Td>
                                            <Td key={2} style={{verticalAlign: "center"}}>
                                                {editingWeekday == day ? (
                                                    <EndTimePicker value={state} onChange={setState}/>
                                                ) : (
                                                    <TimeView time={get(editingDays[day], 1)} fallback="---"/>
                                                )}
                                            </Td>
                                            <Td key={3}>
                                                <Collapse in={editingWeekday == day}>
                                                    <Wrap align="center" direction="column">
                                                        <Button variant="primary" size="sm"
                                                                onClick={() => saveWeekday(day)}
                                                                isDisabled={!timeRowValid}>{t("save")}</Button>
                                                        <Button variant="error" size="sm"
                                                                onClick={() => {
                                                                    resetWeekday(day)
                                                                }}>{t("clear_day")}</Button>
                                                    </Wrap>
                                                </Collapse>
                                                <Collapse in={editingWeekday != day}>
                                                    <Button
                                                        variant="secondary"
                                                        isDisabled={!(editingWeekday == -1 && checkedDays.length == 0)}
                                                        visibility={editingWeekday == -1 && checkedDays.length == 0 ? "initial" : "hidden"}
                                                        size="sm"
                                                        onClick={() => startEditingWeekday(day)}>{t("edit")}</Button>
                                                </Collapse>
                                            </Td>
                                        </Tr>
                                    )
                                })
                            }
                        </Tbody>
                    </Table>
                </CheckboxGroup>
                <AnimatePresence>
                    <Collapse in={editingWeekday < 0 && !editingDesc && !editingConditions && checkedDays.length == 0}>
                        <Wrap mt={3}>
                            <Button variant="primary"
                                    isDisabled={!dealValid}
                                    onClick={saveDeal}
                                    flex={1}
                                    marginEnd="25px">{t("save")}</Button>

                            {initialDeal &&
                                <Button variant="secondary"
                                        flex={1}
                                        onClick={() => setConfirmOpen(true)}>
                                    Discard
                                </Button>
                            }
                            <Button variant="error"
                                    onClick={deleteDeal}>{t("delete", {name: deal.description})}</Button>
                        </Wrap>
                    </Collapse>
                </AnimatePresence>

            </AccordionPanel>
        </AccordionItem>
    )
}